import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Code2, Copy, RotateCcw } from "lucide-react";
import { buildSignature, signaturePlainText, DEFAULT_SIG, SIG_LOGOS } from "@/lib/signatureHtml";
import { copyText } from "@/components/brand/ColourSwatch";

const KEY = "aerosoft-signature";
const FIELDS = [
  ["name", "Full name", "e.g. Jane Citizen", 80],
  ["title", "Job title", "e.g. Director", 80],
  ["phone", "Phone", "e.g. 02 1234 5678", 30],
  ["mobile", "Mobile", "e.g. 0400 000 000", 30],
  ["email", "Email", "name@yourdomain.com.au", 120],
  ["website", "Website", "", 120],
  ["company", "Company", "", 80],
  ["address", "Address line", "", 140],
];
const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-sky-600";
const label = "mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500";

const load = () => {
  try {
    return { ...DEFAULT_SIG, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return DEFAULT_SIG;
  }
};

const copyRich = async (html, text, el) => {
  try {
    await navigator.clipboard.write([new window.ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
  } catch {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    const ok = document.execCommand("copy");
    sel.removeAllRanges();
    if (!ok) throw new Error("copy blocked");
  }
};

export const SignatureBuilder = () => {
  const [v, setV] = useState(load);
  const previewRef = useRef(null);
  const origin = window.location.origin;
  const html = useMemo(() => buildSignature(v, origin), [v, origin]);
  const isLive = /aerosoftsolutions\.com\.au$/i.test(window.location.hostname);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(v));
  }, [v]);

  const copy = async () => {
    if (!v.name.trim()) return toast.error("Add your name first.");
    try {
      await copyRich(html, signaturePlainText(v), previewRef.current);
      toast.success("Signature copied. Paste it into your Outlook or Gmail signature settings.");
    } catch {
      toast.error("Your browser blocked copying. Select the preview with your mouse and copy it instead.", { duration: 30000 });
    }
  };

  return (
    <section className="mt-16" data-testid="sig-builder">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Build your signature</p>
      <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink">Ready-made signature</h2>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">Fill in your details and pick a logo. Your entries are kept in this browser only. Leave a field empty to leave it out.</p>
      {!isLive && <p className="mt-3 max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="sig-not-live">You're on a preview address, so the logo will link here. Build your final signature on aerosoftsolutions.com.au/signature so the logo always loads.</p>}
      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="rounded-[28px] border border-sky-100 bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, l, ph, max]) => (
              <label key={k} className={k === "address" || k === "company" ? "sm:col-span-2" : ""}>
                <span className={label}>{l}</span>
                <input value={v[k]} maxLength={max} placeholder={ph} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={inputCls} data-testid={`sig-input-${k}`} />
              </label>
            ))}
            <label className="sm:col-span-2"><span className={label}>Extra line (optional)</span>
              <textarea rows={2} maxLength={400} value={v.note ?? ""} placeholder="e.g. Proudly Australian owned, or a confidentiality disclaimer" onChange={(e) => setV({ ...v, note: e.target.value })}
                className={`${inputCls} resize-y`} data-testid="sig-input-note" />
              <span className="mt-1 block text-right font-mono text-[11px] text-slate-500" data-testid="sig-note-count">{(v.note ?? "").length}/400</span></label>
            <label className="sm:col-span-2"><span className={label}>Logo</span>
              <select value={v.logo} onChange={(e) => setV({ ...v, logo: e.target.value })} className={inputCls} data-testid="sig-input-logo">
                {Object.entries(SIG_LOGOS).map(([n, o]) => <option key={n} value={n}>{o.label}</option>)}
              </select></label>
          </div>
          <button type="button" onClick={() => setV(DEFAULT_SIG)} className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-ink" data-testid="sig-reset"><RotateCcw className="h-3.5 w-3.5" />Start again</button>
        </div>
        <div className="min-w-0">
          <div className="overflow-x-auto rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8" data-testid="sig-preview-card">
            <p className={label}>Preview</p>
            <div ref={previewRef} className="mt-4 [&_img]:max-w-none" data-testid="sig-preview" dangerouslySetInnerHTML={{ __html: html }} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-white hover:bg-sky-700" data-testid="sig-copy"><Copy className="h-4 w-4" />Copy signature</button>
            <button onClick={() => copyText(html, "Signature HTML")} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-ink hover:border-sky-600 hover:text-sky-700" data-testid="sig-copy-html"><Code2 className="h-4 w-4" />Copy HTML</button>
          </div>
          <div className="mt-5 grid gap-3 text-sm text-slate-700 sm:grid-cols-2" data-testid="sig-instructions">
            <div className="rounded-3xl bg-[#F4F9FD] p-4"><p className="font-mono text-[11px] uppercase tracking-[0.15em] text-sky-700">Outlook</p><p className="mt-1">Settings → Mail → Compose and reply (or File → Options → Mail → Signatures) → new signature → paste.</p></div>
            <div className="rounded-3xl bg-[#F4F9FD] p-4"><p className="font-mono text-[11px] uppercase tracking-[0.15em] text-sky-700">Gmail</p><p className="mt-1">Settings (gear) → See all settings → General → Signature → Create new → paste → Save changes at the bottom.</p></div>
          </div>
        </div>
      </div>
    </section>
  );
};
